/*
 * The visual tour ("omvisning"): one part of the screen at a time, lit up on a
 * dimmed page, with a card beside it that says what it is for.
 *
 * WHO SEES IT. A new account is asked once, after its first sign-in, whether it
 * wants the tour — yes or no. New means the account's user document was created
 * by this app on a first confirmed sign-in (ensureUserDoc in
 * js/services/cloud-sync.js marks it userProfile.tour = 'pending'); accounts
 * from before the tour have no mark and are not asked. The answer goes on the
 * profile, so it is asked once per account rather than once per device. Anyone
 * signed in can take the tour again from the ? button in the top bar.
 *
 * Arbiters and players get different tours, because their sidebars differ. A
 * player account that chose a plan at sign-up is an arbiter on its way: it is
 * left alone until the subscription has made it one (js/utils/tour-logic.js).
 *
 * IT POINTS; IT DOES NOT CLICK. No step switches tab, opens a dialog or changes
 * anything, so the tour can start on any page and leaves the arbiter exactly
 * where they were — the one exception being the phone's sidebar drawer, which
 * the tour opens for the steps inside it and closes again. A step whose element
 * is not on screen (Live, while no tournament is open) is left out.
 *
 * The text is ordinary UI chrome: English source strings through tr(), with
 * the Norwegian in js/i18n/no.js, and the tab names are the tabs' own keys.
 */

import { state } from '../state.js';
import { lastBillingStatus } from './subscription.js';
import {
    tourOfferFor, unionRect, placeTourCard, TOUR_PENDING, TOUR_TAKEN, TOUR_DECLINED
} from '../utils/tour-logic.js';

// How far the lit-up area reaches beyond the element it shows.
const PAD = 6;
// At or below this width the card docks to the top or bottom edge.
const PHONE_WIDTH = 640;
// How long after a step starts its position is followed frame by frame: long
// enough for the phone's sidebar drawer to slide in (0.3s in css/main.css).
const FOLLOW_MS = 700;
const DRAWER_MS = 320;

// The offer waits for the page to settle after sign-in: the account's own
// profile, the billing answer, and any dialog the sign-in itself raises — which
// can arrive a second or two later, after a call to the server.
const OFFER_POLL_MS = 1000;
const OFFER_QUIET_TICKS = 3;
const OFFER_WINDOW_MS = 2 * 60 * 1000;
const BILLING_WAIT_MS = 15 * 1000;

/*
 * A step points at `target` — a selector, or several, everything they match lit
 * up together — or at nothing. `sidebar` marks a target inside the sidebar,
 * which on a phone is a drawer the tour has to open first.
 */
const ARBITER_STEPS = [
    {
        // The tabs themselves, not the whole column: the nav stretches to the
        // foot of the sidebar, and lit up whole it is mostly empty space.
        target: ['#arbiter-nav > .nav-item', '#arbiter-nav > .nav-group'], sidebar: true, icon: 'ri-layout-left-line',
        title: 'Your tabs',
        body: 'Everything in Zugzwang starts from this sidebar. You can drag the tabs into the order you work in.'
    },
    {
        target: '#arbiter-nav .nav-item[data-target="tournaments"]', sidebar: true, icon: 'ri-trophy-line',
        title: 'My Tournaments',
        body: 'Every tournament you run or help run, in folders of your own. Create a new tournament here, or import one from a TRX or TRF file.'
    },
    {
        target: '#arbiter-nav .nav-item[data-target="dashboard"]', sidebar: true, icon: 'ri-dashboard-line',
        title: 'Dashboard',
        body: 'The home page of the tournament you have open: its settings under Options, the tournament check, and shortcuts to the rest.'
    },
    {
        target: ['#nav-players', '#nav-pairings', '#nav-standings'], sidebar: true, icon: 'ri-sword-line',
        title: 'Players, Pairings and Standings',
        body: 'A tournament day: the player list, pairing each round and entering the results, and the standings with tie-breaks and prizes.'
    },
    {
        target: '#nav-live', sidebar: true, icon: 'ri-live-line',
        title: 'Live',
        body: 'Show the games move by move, from DGT boards, LiveChessCloud, Clono or a PGN link.'
    },
    {
        target: '#nav-databases', sidebar: true, icon: 'ri-database-2-line',
        title: 'Player Databases',
        body: 'Your own player registers, with rating lists you control — a club rating, or a school league.'
    },
    {
        target: '#nav-organizers', sidebar: true, icon: 'ri-community-line',
        title: 'Clubs & Organizers',
        body: 'Register the club you run tournaments for, or join it when one of its admins invites you. Its name then appears on your tournaments.'
    },
    {
        target: '#nav-group-others > .nav-group-toggle', sidebar: true, icon: 'ri-folder-3-line',
        title: 'Other Tournaments',
        body: 'Every public tournament: ongoing, upcoming and finished. Open one to see it the way the players do.'
    },
    {
        target: '#btn-notification-bell', icon: 'ri-notification-3-line',
        title: 'Notifications',
        body: 'New registrations, and invitations from other arbiters and from clubs, arrive here.'
    },
    {
        target: '#btn-help-tour', icon: 'ri-question-mark',
        title: 'The tour',
        body: 'Press here whenever you want to take this tour again.'
    },
    {
        target: '#nav-guide', sidebar: true, icon: 'ri-book-open-line',
        title: 'User Guide',
        body: 'Every tab and setting explained in detail. Look here when you wonder what something does.'
    },
    {
        target: '#btn-contact', sidebar: true, icon: 'ri-mail-send-line',
        title: 'Contact',
        body: 'Stuck, or found something that does not work? Send us a message.'
    },
    {
        target: '.sidebar-bottom .nav-item[data-target="settings"]', sidebar: true, icon: 'ri-settings-4-line',
        title: 'Settings',
        body: 'Your profile, subscription, theme, language and keyboard shortcuts.'
    },
    {
        target: null, icon: 'ri-flag-2-line',
        title: 'You are ready',
        body: 'Start by creating a tournament under My Tournaments. Everything in it can be changed later under Options.',
        extra: { label: 'Open the User Guide', run: () => openView('guide') }
    }
];

const PLAYER_STEPS = [
    {
        target: '#nav-my-play', sidebar: true, icon: 'ri-trophy-line',
        title: 'My Tournaments',
        body: 'The tournaments you are playing, have signed up for, and have played.'
    },
    {
        target: '#nav-ongoing', sidebar: true, icon: 'ri-broadcast-line',
        title: 'Ongoing Tournaments',
        body: 'Follow a tournament as it happens: pairings, results and standings, round by round.'
    },
    {
        target: '#nav-upcoming', sidebar: true, icon: 'ri-calendar-event-line',
        title: 'Upcoming Tournaments',
        body: 'Find your next tournament and sign up for it.'
    },
    {
        target: '#nav-finished', sidebar: true, icon: 'ri-history-line',
        title: 'Finished Tournaments',
        body: 'The results of tournaments that are over.'
    },
    {
        target: '#btn-notification-bell', icon: 'ri-notification-3-line',
        title: 'Notifications',
        body: 'News about your registrations arrives here.'
    },
    {
        target: '#btn-help-tour', icon: 'ri-question-mark',
        title: 'The tour',
        body: 'Press here whenever you want to take this tour again.'
    },
    {
        target: '#btn-contact', sidebar: true, icon: 'ri-mail-send-line',
        title: 'Contact',
        body: 'Stuck, or found something that does not work? Send us a message.'
    },
    {
        target: '.sidebar-bottom .nav-item[data-target="settings"]', sidebar: true, icon: 'ri-settings-4-line',
        title: 'Settings',
        body: 'Your profile, language and theme. Want to run tournaments yourself? Choose a plan under Subscription.'
    },
    {
        target: null, icon: 'ri-flag-2-line',
        title: 'You are ready',
        body: 'Find a tournament under Upcoming Tournaments and sign up for it.'
    }
];

// The open offer or tour, or null: { root, card, spotlight, live, onEscape,
// returnFocus, steps, index, openedDrawer, raf }.
let layer = null;

function openView(viewId) {
    if (window.app && typeof window.app.showView === 'function') window.app.showView(viewId);
}

/** The tour for this account: the arbiter's, unless the account is a player. */
function currentRole() {
    const claim = window.authApp && typeof window.authApp.getRole === 'function' ? window.authApp.getRole() : '';
    const role = claim || (state.userProfile && state.userProfile.role) || '';
    return role === 'player' ? 'player' : 'organizer';
}

function isSignedIn() {
    return !!(window.authApp && typeof window.authApp.isAuthenticated === 'function' && window.authApp.isAuthenticated());
}

/** The open question answered, once: 'taken' or 'declined' on the profile. */
function markAnswered(value) {
    const profile = state.userProfile;
    if (!profile || profile.tour !== TOUR_PENDING) return;
    if (window.app && typeof window.app.setUserProfile === 'function') {
        window.app.setUserProfile({ tour: value });
    } else {
        profile.tour = value;
    }
}

// ---------------------------------------------------------------------------
// Finding what a step points at

function isShown(el) {
    if (!el || !el.isConnected) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // An element inside something hidden has no box at all. One in the phone's
    // closed drawer does — off the left edge — and counts: the tour opens it.
    return el.getClientRects().length > 0;
}

/** Every shown element a step's selectors match; a selector may match several. */
function targetsOf(step) {
    if (!step.target) return [];
    const selectors = Array.isArray(step.target) ? step.target : [step.target];
    return selectors.flatMap(sel => Array.from(document.querySelectorAll(sel))).filter(isShown);
}

function stepAvailable(step) {
    return !step.target || targetsOf(step).length > 0;
}

// ---------------------------------------------------------------------------
// The phone's sidebar drawer (opened by #mobile-sidebar-toggle in js/app.js)

const sidebar = document.querySelector('.sidebar');
const sidebarBackdrop = document.getElementById('sidebar-backdrop');

/** The sidebar is a drawer — phones and small tablets (css/main.css). */
function drawerMode() {
    return !!sidebar && getComputedStyle(sidebar).position === 'fixed';
}

function setDrawer(open) {
    if (!sidebar) return;
    sidebar.classList.toggle('active', open);
    if (sidebarBackdrop) sidebarBackdrop.classList.toggle('active', open);
}

// ---------------------------------------------------------------------------
// The layer: a backdrop that catches every click, the lit-up area, and the card

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}

function iconButton(className, iconClass, label) {
    const btn = el('button', className);
    btn.type = 'button';
    btn.title = label;
    btn.setAttribute('aria-label', label);
    btn.appendChild(el('i', iconClass));
    return btn;
}

function textButton(className, label) {
    const btn = el('button', className, label);
    btn.type = 'button';
    return btn;
}

function openLayer(onEscape) {
    // From the offer straight into the tour: focus goes back, at the end, to
    // where it was before the offer appeared.
    const returnFocus = layer ? layer.returnFocus : document.activeElement;
    closeLayer({ restoreFocus: false });

    const root = el('div', 'tour-root tour-no-target');
    const backdrop = el('div', 'tour-backdrop');
    const spotlight = el('div', 'tour-spotlight');
    spotlight.setAttribute('aria-hidden', 'true');
    const card = el('div', 'tour-card');
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'tour-title');
    card.setAttribute('aria-describedby', 'tour-body');
    // Each step is read out as it appears; focus stays on the Next button.
    const live = el('div', 'tour-sr');
    live.setAttribute('aria-live', 'polite');
    root.append(backdrop, spotlight, card, live);
    document.body.appendChild(root);

    layer = {
        root, card, spotlight, live, onEscape, returnFocus,
        steps: null, index: 0, openedDrawer: false, raf: 0, spotlightPlaced: false
    };

    window.addEventListener('keydown', onKeydown, true);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return layer;
}

function closeLayer({ restoreFocus = true } = {}) {
    if (!layer) return;
    const closing = layer;
    layer = null;

    window.removeEventListener('keydown', onKeydown, true);
    window.removeEventListener('resize', position);
    window.removeEventListener('scroll', position, true);
    cancelAnimationFrame(closing.raf);
    if (closing.openedDrawer) setDrawer(false);
    closing.root.remove();

    const back = closing.returnFocus;
    if (restoreFocus && back && back.isConnected && typeof back.focus === 'function') {
        back.focus({ preventScroll: true });
    }
}

/** Where the lit-up area and the card are, for the current step. */
function position() {
    if (!layer) return;
    const step = layer.steps ? layer.steps[layer.index] : null;
    const targets = step ? targetsOf(step) : [];
    const target = unionRect(targets.map(t => t.getBoundingClientRect()), PAD);

    layer.root.classList.toggle('tour-no-target', !target);
    if (target) {
        const s = layer.spotlight.style;
        // The first time, it appears where it belongs instead of gliding in
        // from the corner; after that it moves from step to step.
        const first = !layer.spotlightPlaced;
        if (first) s.transition = 'none';
        s.left = `${target.left}px`;
        s.top = `${target.top}px`;
        s.width = `${target.width}px`;
        s.height = `${target.height}px`;
        if (first) {
            void layer.spotlight.offsetWidth;
            s.transition = '';
            layer.spotlightPlaced = true;
        }
    }

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    // Docked on a phone, the card is as wide as the screen; measure it that way.
    layer.card.classList.toggle('tour-card-docked', !!target && viewport.width <= PHONE_WIDTH);
    const place = placeTourCard({
        target,
        card: { width: layer.card.offsetWidth, height: layer.card.offsetHeight },
        viewport,
        phoneWidth: PHONE_WIDTH
    });
    layer.card.style.left = `${place.left}px`;
    layer.card.style.top = `${place.top}px`;
}

/** Follow the step for a moment while the drawer slides and the page scrolls. */
function follow() {
    if (!layer) return;
    cancelAnimationFrame(layer.raf);
    const until = performance.now() + FOLLOW_MS;
    const tick = () => {
        if (!layer) return;
        position();
        if (performance.now() < until) layer.raf = requestAnimationFrame(tick);
    };
    tick();
}

/** Scroll a target into sight if it is outside it (a short screen's sidebar). */
function bringIntoView(step) {
    const first = targetsOf(step)[0];
    if (!first) return;
    const r = first.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) first.scrollIntoView({ block: 'center' });
}

function onKeydown(e) {
    if (!layer) return;
    if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        layer.onEscape();
        return;
    }
    if (e.key === 'Tab') {
        keepFocusInCard(e);
        e.stopPropagation();
        return;
    }
    if (layer.steps && (e.key === 'ArrowRight' || e.key === 'ArrowLeft') && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        go(e.key === 'ArrowRight' ? 1 : -1);
        return;
    }
    // Every other key stays with the tour: the page's own shortcuts would act on
    // a page the arbiter cannot see properly. Enter and Space still press the
    // focused button — stopping the event's travel does not stop that.
    e.stopPropagation();
}

function keepFocusInCard(e) {
    const focusables = Array.from(layer.card.querySelectorAll('button:not([disabled]), [href]'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const inside = layer.card.contains(document.activeElement);
    if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
    }
}

// ---------------------------------------------------------------------------
// The tour

function renderStep() {
    const step = layer.steps[layer.index];
    const total = layer.steps.length;
    const last = layer.index === total - 1;
    const card = layer.card;
    card.textContent = '';
    // Each step's card fades in where it lands.
    card.classList.remove('tour-card-enter');
    void card.offsetWidth;
    card.classList.add('tour-card-enter');

    const progress = el('div', 'tour-progress');
    const bar = el('span');
    bar.style.width = `${Math.round(((layer.index + 1) / total) * 100)}%`;
    progress.appendChild(bar);
    progress.setAttribute('aria-hidden', 'true');

    const head = el('div', 'tour-card-head');
    const icon = el('span', 'tour-icon');
    icon.appendChild(el('i', step.icon));
    const count = el('span', 'tour-count', tr('Step {n} of {total}', { n: layer.index + 1, total }));
    const close = iconButton('btn-icon tour-close', 'ri-close-line', tr('Close the tour'));
    close.addEventListener('click', () => closeLayer());
    head.append(icon, count, close);

    const title = el('h3', 'tour-title', tr(step.title));
    title.id = 'tour-title';
    const body = el('p', 'tour-body', tr(step.body));
    body.id = 'tour-body';

    const actions = el('div', 'tour-actions');
    if (step.extra) {
        const extra = textButton('btn-outline btn-small tour-extra', tr(step.extra.label));
        extra.addEventListener('click', () => {
            closeLayer({ restoreFocus: false });
            step.extra.run();
        });
        actions.appendChild(extra);
    }
    if (layer.index > 0) {
        const back = textButton('btn-outline btn-small', tr('Back'));
        back.addEventListener('click', () => go(-1));
        actions.appendChild(back);
    }
    const next = textButton('btn-primary btn-small tour-next', last ? tr('Done') : tr('Next'));
    next.addEventListener('click', () => (last ? closeLayer() : go(1)));
    actions.appendChild(next);

    card.append(progress, head, title, body, actions);
    layer.live.textContent = `${tr(step.title)}. ${tr(step.body)}`;

    // The phone's drawer: open for a step inside it, closed again for one outside.
    let wait = 0;
    if (drawerMode()) {
        const open = sidebar.classList.contains('active');
        if (step.sidebar && !open) {
            setDrawer(true);
            layer.openedDrawer = true;
            wait = DRAWER_MS;
        } else if (!step.sidebar && open && layer.openedDrawer) {
            setDrawer(false);
            layer.openedDrawer = false;
        }
    }
    const shown = layer;
    setTimeout(() => {
        if (layer !== shown || layer.steps[layer.index] !== step) return;
        bringIntoView(step);
        follow();
    }, wait);
    follow();
    next.focus({ preventScroll: true });
}

function go(direction) {
    if (!layer || !layer.steps) return;
    let i = layer.index + direction;
    // A step whose element has gone since the tour began is passed over.
    while (i >= 0 && i < layer.steps.length && !stepAvailable(layer.steps[i])) i += direction;
    if (i < 0) return;
    if (i >= layer.steps.length) {
        closeLayer();
        return;
    }
    layer.index = i;
    renderStep();
}

/**
 * Start the tour from its first step. `role` picks the arbiter's or the
 * player's tour; left out, it follows the signed-in account.
 */
export function startTour(role) {
    stopOffer();
    markAnswered(TOUR_TAKEN);
    const all = (role || currentRole()) === 'player' ? PLAYER_STEPS : ARBITER_STEPS;
    const steps = all.filter(stepAvailable);
    if (steps.length === 0) return;

    const opened = openLayer(() => closeLayer());
    opened.steps = steps;
    opened.index = 0;
    renderStep();
}

// ---------------------------------------------------------------------------
// The offer, to a new account

/** Point at the ? button for a moment, so "later" has somewhere to go. */
function hintHelpButton() {
    const btn = document.getElementById('btn-help-tour');
    if (!btn || !isShown(btn)) return;
    btn.classList.remove('tour-hint');
    void btn.offsetWidth; // restart the animation
    btn.classList.add('tour-hint');
    setTimeout(() => btn.classList.remove('tour-hint'), 4000);
}

function answerOffer(yes, role) {
    if (yes) {
        startTour(role);
        return;
    }
    closeLayer();
    markAnswered(TOUR_DECLINED);
    hintHelpButton();
}

function showOffer(role) {
    // Closing it any other way than "Show me around" is a no: it is not asked again.
    const opened = openLayer(() => answerOffer(false));
    const card = opened.card;
    card.classList.add('tour-offer');

    const mark = el('img', 'tour-offer-mark');
    mark.src = '/assets/brand/zugzwang-mark.svg';
    mark.alt = '';
    mark.width = 56;
    mark.height = 56;
    const title = el('h3', 'tour-title', tr('Welcome to Zugzwang'));
    title.id = 'tour-title';
    const body = el('p', 'tour-body', tr('Would you like a quick tour? It shows you where everything is, one step at a time, and takes about a minute. You can also take it later with the ? button at the top.'));
    body.id = 'tour-body';

    const actions = el('div', 'tour-actions');
    const no = textButton('btn-outline', tr('No thanks'));
    no.addEventListener('click', () => answerOffer(false));
    const yes = textButton('btn-primary', tr('Show me around'));
    yes.addEventListener('click', () => answerOffer(true, role));
    actions.append(no, yes);

    card.append(mark, title, body, actions);
    opened.live.textContent = `${tr('Welcome to Zugzwang')}. ${body.textContent}`;
    position();
    yes.focus({ preventScroll: true });
}

/** Nothing else is asking for the arbiter's attention right now. */
function pageIsQuiet() {
    if (document.hidden) return false;
    if (document.body.classList.contains('loading')) return false;
    // Any dialog: sign-in, "Complete your subscription", the guest-registration
    // offer, a confirmation — the tour waits until it is answered.
    if (document.querySelector('.modal-overlay.active')) return false;
    if (sidebar && sidebar.classList.contains('active')) return false;
    // Someone following a tournament is not interrupted.
    if (state.isSpectatorMode) return false;
    return true;
}

let offerTimer = null;

function stopOffer() {
    if (offerTimer) clearInterval(offerTimer);
    offerTimer = null;
}

/** Watch the page after sign-in, and make the offer when there is one to make. */
function scheduleOffer() {
    if (offerTimer) return;
    const since = Date.now();
    let quietTicks = 0;
    offerTimer = setInterval(() => {
        const waited = Date.now() - since;
        if (!isSignedIn() || layer || waited > OFFER_WINDOW_MS) {
            stopOffer();
            return;
        }
        const offer = tourOfferFor({
            profile: state.userProfile,
            claimRole: window.authApp && typeof window.authApp.getRole === 'function' ? window.authApp.getRole() : '',
            billing: lastBillingStatus(),
            billingWaitOver: waited > BILLING_WAIT_MS
        });
        if (offer === null) {
            stopOffer();
            return;
        }
        // A few quiet checks in a row: a dialog the sign-in raises a moment later
        // gets its turn first.
        quietTicks = offer !== 'wait' && pageIsQuiet() ? quietTicks + 1 : 0;
        if (quietTicks < OFFER_QUIET_TICKS) return;
        stopOffer();
        showOffer(offer);
    }, OFFER_POLL_MS);
}

export function initTour() {
    const helpBtn = document.getElementById('btn-help-tour');
    if (helpBtn) helpBtn.addEventListener('click', () => startTour());
    const guideBtn = document.getElementById('btn-guide-tour');
    if (guideBtn) guideBtn.addEventListener('click', () => startTour());

    // Fired by js/services/firebase-auth.js on every sign-in, page loads included.
    document.addEventListener('zw:arbiter-signed-in', scheduleOffer);
    if (window.__zwArbiterSignedIn) scheduleOffer();
}
